<script lang="ts">
  /**
   * La magie du Cartomancien : ses quatre paquets.
   *
   * Il ne choisit pas son Sort : il tire une carte, et la carte ouvre. Le livre
   * fait jouer avec un vrai jeu, et c'est toujours le joueur qui pioche — comme
   * il jette ses dés. Ce que la fiche apporte, c'est la mémoire : ce qui reste
   * dans chaque paquet, ce qui est brûlé, et combien de cartes la journée
   * permet encore.
   *
   * La Défausse est ce qui compte le plus. Elle **persiste d'une séance à
   * l'autre** (Codex IX p.210) : sans la fiche, il faudrait se souvenir trois
   * semaines plus tard des cartes qu'on avait brûlées.
   *
   * Le geste suit la table : toucher la carte tirée, lire ce qu'elle ouvre,
   * puis dire ce qu'elle est devenue.
   */
  import {
    grimoire as lireLeGrimoire,
    magieDuPersonnage,
    type IssueDeSort,
    type MoyenDeRachat,
    type PersonnageEnregistre,
  } from '../../../rules'

  let {
    personnage,
    favori,
    element,
    jouer,
    racheter,
    journee,
  }: {
    personnage: PersonnageEnregistre
    /** Désigne un paquet favori — celui qui porte un Joker — ou le retire. */
    favori: (sphere: string) => void
    /** Choisit un élément d'Union, ou y renonce. */
    element: (element: string) => void
    /** Enregistre la carte tirée et ce qu'elle est devenue. */
    jouer: (sphere: string, carte: string, issue: IssueDeSort, horsLimite: boolean) => void
    /** Reprend une carte de la Défausse. */
    racheter: (sphere: string, carte: string, moyen: MoyenDeRachat) => void
    /** Une nouvelle journée en jeu : la limite repart de zéro. */
    journee: () => void
  } = $props()

  const g = $derived(lireLeGrimoire(personnage))

  /** La carte que le joueur vient de tirer, en attente de ce qu'elle devient. */
  let tiree = $state<{ sphere: string; carte: string } | null>(null)
  /** Enfant du Destin, Volonté transcendante : la pioche ne compte pas. */
  let horsLimite = $state(false)

  const ouvre = $derived(tiree ? magieDuPersonnage(personnage, tiree.carte) : null)
  const sortsOuverts = $derived(ouvre?.spheres.find((s) => s.id === tiree?.sphere)?.sorts ?? [])

  const limiteAtteinte = $derived(g.piochees_du_jour >= g.limite_du_jour)
  /* On peut toucher une carte dès qu'une pioche reste possible — y compris
     hors limite ; on ne peut conclure que si celle-ci l'est vraiment. */
  const peutChoisir = $derived(g.brassard && (!limiteAtteinte || g.hors_limite_possible))
  const peutConclure = $derived(g.brassard && (!limiteAtteinte || horsLimite))

  /* La préparation — favoris et éléments — s'ouvre d'elle-même tant qu'elle
     n'est pas faite : un Cartomancien sans éléments ne sait pas ce qu'il
     lance en Union. */
  const aPreparer = $derived(
    g.favoris_choisis < g.favoris_attendus || g.elements_choisis < g.elements_attendus,
  )
  let preparer = $state(false)
  const montrerPreparation = $derived(preparer || aPreparer)

  /** La Défausse dépliée, une Sphère à la fois. */
  let defausseOuverte = $state<string | null>(null)

  function conclure(issue: IssueDeSort) {
    if (!tiree) return
    jouer(tiree.sphere, tiree.carte, issue, horsLimite)
    tiree = null
    horsLimite = false
  }

  const NOMS: Record<string, string> = {
    valet: 'Valet',
    dame: 'Dame',
    roi: 'Roi',
    as: 'As',
    joker: 'Joker',
  }
  const COULEURS: Record<string, string> = {
    coeur: '♥',
    trefle: '♣',
    carreau: '♦',
    pique: '♠',
  }
  /** Le nom de la couleur : on tire le 7 de Trèfle, pas le 7 de « Sphère d'Union ». */
  const NOMS_DE_COULEUR: Record<string, string> = {
    coeur: 'Cœur',
    trefle: 'Trèfle',
    carreau: 'Carreau',
    pique: 'Pique',
  }
  const rouge = (couleur: string) => couleur === 'coeur' || couleur === 'carreau'
</script>

<div class="cartouche">
  <div class="bandeau">Magie</div>
  <div class="corps">
    <p class="regle">
      Vous piochez dans votre jeu ; la fiche retient ce que vous avez tiré.
      Touchez la carte tirée pour lire ce qu'elle ouvre, puis dites ce qu'elle
      est devenue. La Défausse se garde d'une séance à l'autre.
    </p>

    {#if !g.brassard}
      <p class="alerte">
        Aucun Brassard sur soi : sans lui, impossible d'user des Sphères. Il se
        porte depuis le Sac.
      </p>
    {/if}

    <!-- La journée : ce qui a été pioché, et ce que la limite permet. -->
    <div class="journee">
      <span class="compte data" class:atteinte={limiteAtteinte}>
        {g.piochees_du_jour} / {g.limite_du_jour}
      </span>
      <span class="detail">cartes piochées aujourd'hui — {g.limite_detail}</span>
      <span class="gestes">
        <button type="button" class="pilule" onclick={journee}>Nouvelle journée</button>
        {#if !aPreparer}
          <button type="button" class="lien" onclick={() => (preparer = !preparer)}>
            {preparer ? 'Refermer la préparation' : 'Préparer le jeu'}
          </button>
        {/if}
      </span>
    </div>
    {#if limiteAtteinte && g.brassard}
      <p class="detail">
        Limite du jour atteinte.
        {#if g.hors_limite_possible}
          Seule une pioche hors limite reste possible.
        {:else}
          Une nouvelle journée la remettra à zéro.
        {/if}
      </p>
    {/if}

    {#if montrerPreparation}
      <section class="preparation">
        <h3>
          Paquets favoris
          <span class="rendement">{g.favoris_choisis} / {g.favoris_attendus}</span>
        </h3>
        <p class="detail">
          Chacun reçoit un Joker, qui vaut n'importe quelle carte de sa Sphère —
          sauf l'As.
        </p>
        <div class="choix">
          {#each g.paquets as p (p.sphere)}
            <button
              type="button"
              class="pilule"
              aria-pressed={p.favori}
              onclick={() => favori(p.sphere)}
            >
              <span class="couleur" class:rouge={rouge(p.couleur)}>{COULEURS[p.couleur]}</span>
              {p.nom}
            </button>
          {/each}
        </div>

        <h3>
          Éléments d'Union
          <span class="rendement">{g.elements_choisis} / {g.elements_attendus}</span>
        </h3>
        <p class="detail">
          Deux à la Création, un de plus aux Rangs 3 et 4. Seuls leurs Sorts
          s'offrent au Trèfle.
        </p>
        <div class="choix">
          {#each g.elements as el (el.id)}
            <button
              type="button"
              class="pilule"
              aria-pressed={el.choisi}
              onclick={() => element(el.id)}
            >
              {el.nom}
            </button>
          {/each}
        </div>
      </section>
    {/if}

    {#each g.paquets as p (p.sphere)}
      <section class="paquet">
        <h3>
          <span class="couleur" class:rouge={rouge(p.couleur)}>{COULEURS[p.couleur]}</span>
          {p.nom}
          {#if p.favori}<span class="voie">favori</span>{/if}
          <span class="rendement">{p.pioche.length} / {p.composition} en pioche</span>
        </h3>

        <div class="tirage" role="group" aria-label="La pioche de {p.nom}">
          {#each p.pioche as c (c.carte)}
            {@const touchee = tiree?.sphere === p.sphere && tiree?.carte === c.carte}
            <button
              type="button"
              class="jet carte"
              class:joker={c.carte === 'joker'}
              aria-pressed={touchee}
              disabled={!peutChoisir && !touchee}
              onclick={() => {
                tiree = touchee ? null : { sphere: p.sphere, carte: c.carte }
                horsLimite = false
              }}
            >
              {NOMS[c.carte] ?? c.carte}
            </button>
          {:else}
            <span class="detail">Tout est à la Défausse.</span>
          {/each}
        </div>

        {#if tiree?.sphere === p.sphere && ouvre}
          <div class="tiree">
            <p class="detail">
              <strong>
                {NOMS[tiree.carte] ?? tiree.carte} de {NOMS_DE_COULEUR[p.couleur] ?? p.nom}
              </strong>
              {#if ouvre.niveau}
                — Niveau de puissance {ouvre.niveau}.
              {:else}
                — il vaut n'importe quelle carte de la Sphère, sauf l'As.
              {/if}
            </p>
            {#if sortsOuverts.length === 0}
              <p class="detail">Cette carte n'ouvre rien ici.</p>
            {:else}
              <ul class="achats larges">
                {#each sortsOuverts as sort (sort.id)}
                  <li>
                    <span class="nom">
                      {sort.nom}
                      {#if sort.element}<span class="voie">{sort.element}</span>{/if}
                      <span class="exigence">{sort.resume}</span>
                    </span>
                    <span class="chiffres data">p. {sort.page}</span>
                    <span></span>
                  </li>
                {/each}
              </ul>
            {/if}

            <p class="regle">
              Lancer : un Test d'Incantation, une Demi-action, portée Moyenne.
              Qu'est devenue la carte ?
            </p>
            {#if g.hors_limite_possible}
              <label class="case">
                <input type="checkbox" bind:checked={horsLimite} />
                Hors limite — Enfant du Destin, Volonté transcendante
              </label>
            {/if}
            <span class="gestes">
              <button
                type="button"
                class="pilule"
                disabled={!peutConclure}
                onclick={() => conclure('defaussee')}
              >
                À la Défausse
              </button>
              <button
                type="button"
                class="pilule"
                disabled={!peutConclure}
                onclick={() => conclure('revient')}
              >
                Réussite critique — elle revient
              </button>
              <button
                type="button"
                class="pilule discret"
                disabled={!peutConclure}
                onclick={() => conclure('echec_critique')}
              >
                Échec critique — Paralysé
              </button>
              <button type="button" class="lien" onclick={() => (tiree = null)}>
                Je ne l'ai pas tirée
              </button>
            </span>
            <p class="detail">
              « À la Défausse » vaut pour une réussite, un échec ou un Sort
              auquel on renonce : la carte part dans les trois cas. Seule la
              réussite critique la remet dans le paquet.
            </p>
          </div>
        {/if}

        {#if p.defausse.length > 0}
          <button
            type="button"
            class="lien"
            aria-expanded={defausseOuverte === p.sphere}
            onclick={() => (defausseOuverte = defausseOuverte === p.sphere ? null : p.sphere)}
          >
            Défausse : {p.defausse.length} carte{p.defausse.length > 1 ? 's' : ''}
          </button>
          {#if defausseOuverte === p.sphere}
            <!-- Racheter au prix de la table (p.211), ou récupérer sans rien
                 payer : une Source de Magie, une mission, le Cœur ésotérique. -->
            <ul class="plates defausse">
              {#each p.defausse as d (d.carte)}
                <li>
                  <span class="nom">{NOMS[d.carte] ?? d.carte}</span>
                  <span class="gestes">
                    <button
                      type="button"
                      class="pilule"
                      disabled={g.pi_disponibles < d.prix_pi}
                      onclick={() => racheter(p.sphere, d.carte, 'influence')}
                    >
                      <span class="data">{d.prix_pi} PI</span>
                    </button>
                    <button
                      type="button"
                      class="pilule"
                      disabled={(g.argent ?? 0) < d.prix_livres}
                      onclick={() => racheter(p.sphere, d.carte, 'livres')}
                    >
                      <span class="data">{d.prix_livres} £</span>
                    </button>
                    <button
                      type="button"
                      class="pilule discret"
                      onclick={() => racheter(p.sphere, d.carte, 'offerte')}
                    >
                      Récupérée
                    </button>
                  </span>
                </li>
              {/each}
            </ul>
          {/if}
        {/if}
      </section>
    {/each}

    {#if ouvre}
      <section class="paquet">
        <h3>Combinaisons d'éléments</h3>
        <p class="regle">
          Même cible, même Manche, et dans cet ordre : Eau puis Foudre double
          les Dégâts, Foudre puis Eau ne fait rien. Un élément déjà présent — la
          pluie — tient lieu de premier Sort.
        </p>
        <ul class="plates">
          {#each ouvre.combinaisons as c (c.premier + c.second)}
            <li>
              <span class="quoi">
                <span class="nom">{c.premier} → {c.second}</span>
                <span class="detail">{c.effet}</span>
              </span>
            </li>
          {/each}
        </ul>
      </section>
    {/if}
  </div>
</div>

<style>
  .cartouche {
    margin-bottom: calc(var(--pas) * 3);
  }
  .cartouche .corps {
    padding: calc(var(--pas) * 3);
    display: grid;
    gap: calc(var(--pas) * 2.5);
  }

  /* La journée : le compte en grand, la raison en petit. */
  .journee {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: var(--pas) calc(var(--pas) * 2);
  }
  .journee .compte {
    font-size: 1.4rem;
    font-weight: 600;
  }
  .journee .compte.atteinte {
    color: var(--ko);
  }
  .journee .gestes {
    display: flex;
    align-items: center;
    gap: var(--pas);
    margin-left: auto;
  }
  .alerte {
    color: var(--ko);
    font-size: 0.85rem;
    margin: 0;
  }

  .preparation,
  .paquet {
    display: grid;
    gap: var(--pas);
  }
  .preparation {
    padding: calc(var(--pas) * 2);
    border: 1px dashed var(--line);
    border-radius: var(--r);
  }
  .paquet {
    border-top: 1px solid var(--line);
    padding-top: calc(var(--pas) * 2);
  }
  .choix {
    display: flex;
    flex-wrap: wrap;
    gap: var(--pas);
  }

  /* La pioche : une rangée de cartes, la carte tirée en évidence. */
  .tirage {
    display: flex;
    flex-wrap: wrap;
    gap: calc(var(--pas) / 2);
  }
  .carte {
    min-width: 3.2rem;
    padding: calc(var(--pas) * 1.5) var(--pas);
  }
  .carte[aria-pressed='true'] {
    outline: 2px solid var(--gold);
  }
  .carte.joker {
    font-style: italic;
  }

  /* La carte tirée, en retrait sous son paquet : ce qu'elle ouvre, et le
     verdict qu'on attend. */
  .tiree {
    display: grid;
    gap: var(--pas);
    padding: var(--pas) 0 var(--pas) calc(var(--pas) * 2);
    border-left: 2px solid var(--gold);
  }
  .tiree .gestes {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--pas);
  }
  .case {
    display: flex;
    align-items: center;
    gap: var(--pas);
    font-size: 0.85rem;
  }

  .defausse li {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: var(--pas);
  }
  .defausse .gestes {
    display: flex;
    gap: calc(var(--pas) / 2);
  }

  h3 {
    margin: 0;
    font-family: var(--titre);
    font-size: 0.9rem;
    display: flex;
    align-items: baseline;
    gap: var(--pas);
    flex-wrap: wrap;
  }
  /* Le pique et le trèfle sont noirs, le cœur et le carreau rouges. */
  .couleur {
    font-size: 1.1rem;
    color: var(--ink);
  }
  .couleur.rouge {
    color: var(--ko);
  }
  .rendement {
    font-size: 0.72rem;
    color: var(--ink-soft);
    font-family: system-ui, -apple-system, 'Segoe UI', sans-serif;
  }
</style>
